import { omit } from 'lodash';
import moment from 'moment';
import { useRouter } from 'next/router';
import React, { Fragment, useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Button } from 'Components/frameworks/button/Button';
import { getFacetLocalizedLabel } from 'Components/widgets/criteria/criteria-facets/search-facets/SearchFacets.utils';
import { t } from 'Src/i18n';
import {
  customFiltersSelector,
  filtersSelector,
  searchQuerySelector,
} from 'Store/faceting/faceting.selector';
import {
  changeKidsNumber,
  resetCriteria,
  setKidsValidationState,
  setSearchQuery,
  updateCustomFilters,
  updateFilters,
} from 'Store/faceting/facetingSearch.action';
import { setKeywords } from 'Store/search/query/searchQuery.action';
import { keywordsSelector } from 'Store/search/search.selector';
import { setDepartureCity } from 'Store/trip-criteria/departure-city/departureCity.action';
import { setDepartureDate } from 'Store/trip-criteria/departure-date/departureDate.action';
import { DEFAULT_TRIP_DURATION } from 'Store/trip-criteria/duration/duration.constants';
import {
  getDepartureCity,
  getDepartureDate,
  getTripDuration,
} from 'Store/trip-criteria/tripCriteria.selector';
import { setAdultsNumber } from 'Store/trip-information/adults/adults.action';
import { setKids } from 'Store/trip-information/kids/kids.action';
import { getAdultsNumber, getKids } from 'Store/trip-information/storeHelper';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import {
  ACTIVITIES_FACET_ID,
  COUNTRIES_FACET_ID,
  DEPARTURE_CITY_FACET_ID,
  DESTINATIONS_FACET_ID,
  FLIGHT_DURATION_FACET_ID,
  FLIGHT_DURATION_FACET_LABELS,
  SEASON_FACET_ID,
  SEASON_FACET_LABELS,
  TYPES_OF_STAY_FACET_ID,
  TYPES_OF_STAY_FACET_LABELS,
} from 'Utils/faceting/constants';
import { CriteriaControl } from './CriteriaControl';
export const CriteriaControls = ({
  setShouldNudge,
}: {
  setShouldNudge?: (arg0: boolean) => void;
}) => {
  const searchQuery = useSelector(searchQuerySelector);
  const keywords = useSelector(keywordsSelector);
  const facetCustomFilters = useSelector(customFiltersSelector);
  const facettesFilters = useSelector(filtersSelector);
  const [searchQueryLabel, setSearchQueryLabel] = useState(searchQuery?.label);
  const [customFacets, setCustomFacets] = useState(facetCustomFilters);
  const [facettes, setFacettes] = useState(facettesFilters);
  const departureDate = useSelector(getDepartureDate);
  const [departureDateCriteria, setDepartureDateCriteria] = useState(departureDate);
  const tripDuration = useSelector(getTripDuration);
  const adultsNumber = useSelector(getAdultsNumber);
  const kids = useSelector(getKids);
  const [adultsNumberState, setAdultsNumberState] = useState(adultsNumber);
  const departureCityCode = useSelector(getDepartureCity);
  const { query } = useRouter();
  const queryParams = omit(query, ['dynamicPage']);
  const [previousQueryParams, setPreviousQueryParams] = useState(JSON.stringify(queryParams));
  useEffect(() => {
    setCustomFacets(facetCustomFilters);
  }, [JSON.stringify(facetCustomFilters)]);
  useEffect(() => {
    setFacettes(facettesFilters);
    setAdultsNumberState(adultsNumber);
  }, [JSON.stringify(facettesFilters), adultsNumber]);
  const dispatch = useDispatch();
  let labelParts = '';

  if (adultsNumberState > 0) {
    const labelPartsTmp = [];
    const lengthPeople = adultsNumber + kids.length;

    if (lengthPeople) {
      if (adultsNumber >= 1) {
        labelPartsTmp.push(
          t('booking.form.selectAdultsLabels', {
            count: adultsNumber,
          }),
        );
      }

      if (kids.length >= 1) {
        labelPartsTmp.push(
          t('booking.form.selectChildrenLabels', {
            count: kids.length,
          }),
        );
      }

      labelParts = labelPartsTmp.join(' - ');
    }
  }

  const isFromAutoComplete = Boolean(searchQuery.type);
  const searchTerms = isFromAutoComplete || searchQuery.label ? searchQuery.label : keywords;
  const track = useTracking({
    categoryGA: 'search',
    actionGA: 'search',
    searchReferrer: 'search',
    searchTerms: searchTerms || '',
  });

  const removeDepartureDateCriteria = () => {
    setPreviousQueryParams('');
    setDepartureDateCriteria(null);
  };

  const removeFacetCriteria = ({ id, name }) => {
    setPreviousQueryParams('');
    setCustomFacets(
      [...customFacets].filter(
        ({ id: facetId, name: facetName }) => id !== facetId || name !== facetName,
      ),
    );
    setFacettes(
      [...facettes].filter(
        ({ id: facetteId, name: facetteName }) => id !== facetteId || name !== facetteName,
      ),
    );
  };

  const clearSearchQuery = () => {
    setPreviousQueryParams('');
    setSearchQueryLabel('');
  };

  const submitFilters = () => {
    track({
      labelGA: 'noResult_applyCriterias',
    });
    setShouldNudge && setShouldNudge(false);
    batch(() => {
      dispatch(updateCustomFilters(customFacets));
      dispatch(updateFilters(facettes));

      if (!departureDateCriteria) {
        dispatch(setDepartureDate(null));
      }

      if (searchQueryLabel !== searchQuery?.label) {
        if (keywords) {
          dispatch(setKeywords(''));
        }

        dispatch(
          setSearchQuery({
            label: '',
          }),
        );
      }

      const previousDepartureCity = facettesFilters.find(
        ({ id }) => id === DEPARTURE_CITY_FACET_ID,
      );
      const departureCity = facettes.find(({ id }) => id === DEPARTURE_CITY_FACET_ID);

      if (!departureCity && previousDepartureCity) {
        dispatch(
          setDepartureCity({
            value: null,
          }),
        );
      }

      if (!adultsNumberState && adultsNumber) {
        dispatch(setAdultsNumber(0));
        dispatch(setKids([]));
        dispatch(changeKidsNumber(0));
        dispatch(setKidsValidationState([]));
      }

      if (JSON.stringify(queryParams) === previousQueryParams) {
        setShouldNudge && setShouldNudge(true);
      } else {
        setShouldNudge && setShouldNudge(false);
      }
    });
    setPreviousQueryParams(JSON.stringify(queryParams));
  };

  const cleanCriteria = async () => {
    track({
      labelGA: 'noResult_resetCriterias',
    });
    batch(() => {
      if (adultsNumberState) {
        dispatch(setAdultsNumber(0));
        dispatch(setKids([]));
        dispatch(changeKidsNumber(0));
        dispatch(setKidsValidationState([]));
      }

      if (keywords) {
        dispatch(setKeywords(''));
      }
    });
    setShouldNudge && setShouldNudge(false);
    return dispatch(resetCriteria());
  };

  const clearParticipants = () => {
    setPreviousQueryParams('');
    setAdultsNumberState(0);
  };

  const noTransport = departureCityCode === 'NO';
  return (
    <Fragment>
      {searchQueryLabel && (
        <CriteriaControl onClear={clearSearchQuery}>{searchQueryLabel}</CriteriaControl>
      )}
      {departureDateCriteria && (
        <CriteriaControl onClear={removeDepartureDateCriteria} id="departure_date">
          {`${moment(departureDateCriteria).format('DD MMM')} - ${moment(departureDateCriteria)
            .add({
              days: tripDuration || DEFAULT_TRIP_DURATION,
            })
            .format('DD MMM')}`}
        </CriteriaControl>
      )}
      {!!labelParts && (
        <CriteriaControl onClear={clearParticipants} key="participants" id="participants">
          {labelParts}
        </CriteriaControl>
      )}
      {facettes
        .filter(({ id }) => id === DEPARTURE_CITY_FACET_ID)
        .map(({ id, name, label }) => (
          <CriteriaControl
            onClear={() =>
              removeFacetCriteria({
                id,
                name,
              })
            }
            key={name}
            id={name}
          >
            {noTransport ? label : `${t('common.transport.from')} ${label}`}
          </CriteriaControl>
        ))}
      {facettes
        .filter(({ id }) => id === DESTINATIONS_FACET_ID || id === COUNTRIES_FACET_ID)
        .map(({ id, name }) => (
          <CriteriaControl
            onClear={() =>
              removeFacetCriteria({
                id,
                name,
              })
            }
            key={name}
            id={name}
          >
            {name}
          </CriteriaControl>
        ))}
      {facettes
        .filter(({ id }) => id === ACTIVITIES_FACET_ID)
        .map(({ id, name }) => (
          <CriteriaControl
            onClear={() =>
              removeFacetCriteria({
                id,
                name,
              })
            }
            key={name}
            id={name}
          >
            {name.split('|')[1]}
          </CriteriaControl>
        ))}
      {customFacets
        .filter(({ id }) => id === FLIGHT_DURATION_FACET_ID)
        .map(({ id, name }) => {
          const facetLabel = getFacetLocalizedLabel({
            facetLabels: FLIGHT_DURATION_FACET_LABELS,
            facetValue: name,
          });
          return (
            <CriteriaControl
              key={name}
              id={name}
              onClear={() =>
                removeFacetCriteria({
                  id,
                  name,
                })
              }
            >
              {facetLabel}
            </CriteriaControl>
          );
        })}
      {facettes
        .filter(({ id }) => id === SEASON_FACET_ID)
        .map(({ id, name }) => {
          const facetLabel = getFacetLocalizedLabel({
            facetLabels: SEASON_FACET_LABELS,
            facetValue: name,
          });
          return (
            <CriteriaControl
              key={name}
              id={name}
              onClear={() =>
                removeFacetCriteria({
                  id,
                  name,
                })
              }
            >
              {facetLabel}
            </CriteriaControl>
          );
        })}
      {facettes
        .filter(({ id }) => id === TYPES_OF_STAY_FACET_ID)
        .map(({ id, name }) => {
          const facetLabel = getFacetLocalizedLabel({
            facetLabels: TYPES_OF_STAY_FACET_LABELS,
            facetValue: name,
          });
          return (
            <CriteriaControl
              key={name}
              id={name}
              onClear={() =>
                removeFacetCriteria({
                  id,
                  name,
                })
              }
            >
              {facetLabel}
            </CriteriaControl>
          );
        })}
      <div className="mt-15 flex flex-col-reverse justify-between md:flex md:flex-row">
        <button
          className="mt-15 text-14 text-black underline md:mt-0"
          data-testid="reset-filters"
          onClick={cleanCriteria}
          type="button"
        >
          {t('common.comparator.resetCriteria')}
        </button>
        <Button data-testid="apply-filters" onClick={submitFilters} size="medium">
          {t('common.apply')}
        </Button>
      </div>
    </Fragment>
  );
};
